import type { PageMeta } from '../content/pageMeta';

/** Published page slugs — keys of `PageMeta.slug` and bootstrap routing. */
export const PAGE_SLUG = {
  index: 'index',
  menu: 'menu',
  aboutUs: 'about-us',
  contact: 'contact',
  impressum: 'impressum',
  datenschutz: 'datenschutz',
} as const;

export const PAGE_SLUG_KEY_VALUES = [
  PAGE_SLUG.index,
  PAGE_SLUG.menu,
  PAGE_SLUG.aboutUs,
  PAGE_SLUG.contact,
  PAGE_SLUG.impressum,
  PAGE_SLUG.datenschutz,
] as const;

export type PageSlug = (typeof PAGE_SLUG_KEY_VALUES)[number];

/** @deprecated Use `PAGE_SLUG.index` */
export const HOMEPAGE_SLUG = PAGE_SLUG.index;

/**
 * Strips a leading locale segment when present (`/en/menu` → `/menu`).
 * Caller should run locale stripping before slug resolution when using proxy routing.
 */
export function normalizePagePath(pathname: string): string {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed === '/') {
    return '/';
  }
  return trimmed.endsWith('/') && trimmed.length > 1 ? trimmed.slice(0, -1) : trimmed;
}

/**
 * Resolves a URL path to a page slug (G-5).
 * Bare root `/` maps to `index`; no `/index` canonical path is emitted.
 */
export function resolveSlugFromPath(pathname: string): string {
  const normalized = normalizePagePath(pathname);
  if (normalized === '/') {
    return HOMEPAGE_SLUG;
  }
  const segments = normalized.split('/').filter(Boolean);
  const slug = segments[segments.length - 1];
  return slug ?? HOMEPAGE_SLUG;
}

export function resolvePageBySlug(pages: PageMeta[], slug: string): PageMeta | undefined {
  return pages.find((page) => page.slug === slug);
}

export function resolvePageFromPath(
  pathname: string,
  pages: PageMeta[],
): PageMeta | undefined {
  const slug = resolveSlugFromPath(pathname);
  return resolvePageBySlug(pages, slug);
}

/** Public href for a page slug (homepage → `/`). */
export function publicPathForPageSlug(slug: string, localePrefix?: string): string {
  const prefix = localePrefix?.replace(/\/$/, '') ?? '';
  if (slug === HOMEPAGE_SLUG) {
    return prefix ? `${prefix}/` : '/';
  }
  return prefix ? `${prefix}/${slug}` : `/${slug}`;
}

/**
 * Prefixes absolute site paths for path-based previews
 * (`/api/serve-staging/{id}`, `/api/serve-edge/{domain}`).
 * Anchors and external URLs are left unchanged.
 */
export function prefixPublicPath(href: string, pathPrefix?: string | null): string {
  if (!pathPrefix || !href.startsWith('/') || href.startsWith('//')) {
    return href;
  }
  const base = pathPrefix.replace(/\/$/, '');
  if (!base) {
    return href;
  }
  if (href === '/') {
    return base;
  }
  return `${base}${href}`;
}

/**
 * Strips a leading supported-locale segment (`/en/menu` → `/menu`).
 * Does not treat `defaultLocale` specially — any catalog match is stripped.
 */
export function stripLocalePrefixFromPathname(
  pathname: string,
  supportedLocales: readonly string[],
): string {
  const normalized = normalizePagePath(pathname);
  const supported = new Set(
    supportedLocales.map((code) => code.trim().toLowerCase()).filter(Boolean),
  );
  const segments = normalized.split('/').filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (!first || !supported.has(first)) {
    return normalized;
  }
  const rest = segments.slice(1);
  return rest.length === 0 ? '/' : `/${rest.join('/')}`;
}

/**
 * Locale-prefixed public path for tenant sites (§4.1).
 * Default locale → unprefixed; others → `/{locale}` or `/{locale}{path}`.
 */
export function buildLocalizedSitePath(
  strippedPath: string,
  locale: string,
  defaultLocale: string,
): string {
  const path = normalizePagePath(strippedPath);
  const loc = locale.trim().toLowerCase();
  const def = defaultLocale.trim().toLowerCase();
  if (!loc || loc === def) {
    return path;
  }
  if (path === '/') {
    return `/${loc}`;
  }
  return `/${loc}${path}`;
}

/**
 * Homepage section link that stays correct from any route (Imprint, Menu page, etc.).
 * Default locale → `/#menu` (or `{prefix}#menu`); other locales → `/en#menu`.
 */
export function buildHomepageAnchorHref(
  anchor: string,
  locale: string,
  defaultLocale: string,
  publicPathPrefix?: string | null,
): string {
  const hash = anchor.startsWith('#') ? anchor : `#${anchor}`;
  const homePath = buildLocalizedSitePath('/', locale, defaultLocale);
  return `${prefixPublicPath(homePath, publicPathPrefix)}${hash}`;
}

/**
 * If `href` is a homepage section hash (`#menu`, `/#menu`, `/en#menu`), rebuild it for
 * the active locale. Returns null for page paths, external URLs, and non-hash hrefs.
 */
export function normalizeHomepageSectionHref(
  href: string,
  locale: string,
  defaultLocale: string,
  publicPathPrefix?: string | null,
): string | null {
  const trimmed = href.trim();
  if (!trimmed) {
    return null;
  }

  if (/^#[A-Za-z][\w-]*$/.test(trimmed)) {
    return buildHomepageAnchorHref(trimmed, locale, defaultLocale, publicPathPrefix);
  }

  let pathAndHash = trimmed;
  const prefix = publicPathPrefix?.replace(/\/$/, '');
  if (prefix && pathAndHash.startsWith(prefix)) {
    pathAndHash = pathAndHash.slice(prefix.length) || '/';
  }

  const match = pathAndHash.match(/^\/(?:[a-zA-Z]{2}\/?)?(#[A-Za-z][\w-]*)$/);
  if (match?.[1]) {
    return buildHomepageAnchorHref(match[1], locale, defaultLocale, publicPathPrefix);
  }

  return null;
}

/** Locale switcher href for the current page (path-preserving). */
export function buildLocaleSwitcherHref(input: {
  targetLocale: string;
  defaultLocale: string;
  supportedLocales: readonly string[];
  /** Current browser/path (may include locale prefix). */
  pathname?: string | null;
  publicPathPrefix?: string | null;
}): string {
  const pathname = input.pathname?.trim() || '/';
  const stripped = stripLocalePrefixFromPathname(pathname, input.supportedLocales);
  const localized = buildLocalizedSitePath(
    stripped,
    input.targetLocale,
    input.defaultLocale,
  );
  return prefixPublicPath(localized, input.publicPathPrefix);
}
